// On-behalf-of Sales Rep helpers for the account-setup flow.
//
// When an internal rep answers "Is this sale on behalf of another Sales or
// Partner?" with "Sales Rep" (salesRepOrPartner === 'sales-rep') and picks a rep
// via the "Search by Name" picker, the selected Salesforce user is stored on the
// location's form values as `salesRepInfo.repInfo` (see AsyncSearchField).
//
// The order-create payload builder in new-order.js reads the legacy
// `order.accountInformation.{differentSalesRep,behalf,behalfFirst,behalfLast,
// behalfEmail}` shape and turns `behalf` into `data.behalfRepId`
// (→ cc_order.behalf_rep_id → Recurly On_Behalf_Of custom field). The new
// account-setup flow never populated those fields, so On_Behalf_Of was empty.
// These helpers rebuild that legacy shape from the account-setup form values.
//
// NOTE: this does NOT affect Sales_Rep_ID — the backend sets that to the
// logged-in user (auth.network_id) regardless of what the client sends.

// Resolve the rep's network id used for On_Behalf_Of. Prefer the Salesforce
// FederationIdentifier (the canonical network id for federated Thryv reps). Some
// Salesforce users — including test users — have a null FederationIdentifier; in
// that case fall back to the local part of a @thryv.com email, which IS the
// network id by convention (network_id@thryv.com). Returns null when neither is
// available (e.g. a non-thryv email with no FederationIdentifier).
const resolveRepNetworkId = (repInfo = {}) => {
  if (repInfo.FederationIdentifier) return repInfo.FederationIdentifier
  const email = String(repInfo.Email || '').trim()
  return email.toLowerCase().endsWith('@thryv.com') ? email.split('@')[0] : null
}

// Return the selected Salesforce rep object only when an on-behalf Sales Rep was
// actually chosen; otherwise null (self-sale / partner / no selection). Presence
// of a picked rep is enough — the network-id value is resolved separately so a
// null FederationIdentifier doesn't silently drop the whole selection.
export const getBehalfRepInfo = (values = {}) => {
  if (values.salesRepOrPartner !== 'sales-rep') return null
  const repInfo = values.salesRepInfo?.repInfo
  if (!repInfo || (!repInfo.Id && !repInfo.Name)) return null
  return repInfo
}

// Build the legacy on-behalf-of fields for order.accountInformation. Returns an
// empty object when no on-behalf rep is selected (or no network id could be
// resolved) so it can be safely spread.
export const buildBehalfRepFields = (values = {}) => {
  const repInfo = getBehalfRepInfo(values)
  if (!repInfo) return {}
  const behalf = resolveRepNetworkId(repInfo)
  if (!behalf) return {}
  return {
    differentSalesRep: {value: true},
    behalf,
    behalfFirst: repInfo.FirstName || '',
    behalfLast: repInfo.LastName || '',
    behalfEmail: repInfo.Email || '',
  }
}

// Multi-location: the purchase link is for the child, so prefer the child's
// on-behalf selection and fall back to the parent's. Prefer whichever actually
// yields fields (a picked-but-unresolvable child shouldn't hide the parent's).
export const buildBehalfRepFieldsForChildOrder = (
  childValues = {},
  parentValues = {},
) => {
  const child = buildBehalfRepFields(childValues)
  return Object.keys(child).length ? child : buildBehalfRepFields(parentValues)
}

// Upgrade order flow reads the on-behalf rep as `salesRepCode` (the rep's
// network id) plus behalf* on the order/accountInfo — a different key from the
// new-order `behalf`. Same selection source (getBehalfRepInfo) + network-id
// resolution so the account-setup / edit-location picker flows into the upgrade
// purchase too (salesRepCode → data.behalfRepId → cc_order.behalf_rep_id →
// Recurly On_Behalf_Of). Returns an empty object when nothing to attribute.
export const buildBehalfRepUpgradeFields = (values = {}) => {
  const repInfo = getBehalfRepInfo(values)
  if (!repInfo) return {}
  const salesRepCode = resolveRepNetworkId(repInfo)
  if (!salesRepCode) return {}
  return {
    differentSalesRep: {value: true},
    salesRepCode,
    behalfFirst: repInfo.FirstName || '',
    behalfLast: repInfo.LastName || '',
    behalfEmail: repInfo.Email || '',
  }
}
